import { ZollAPIService } from '../../../services/zollApi.service'
import { zollBaseURL } from '../../../services/api.service'
import { AuthenticationResponse, GetUserTypeResponse } from '../store/login.types'
import { AuthState } from '@okta/okta-auth-js'
import { IUserState } from '../../user/store/user.types'
import { decodeAccessToken, isOffline } from '../../../utilities'
import { resetUser } from '../../user/store/user.actions'
import { Dispatch } from 'react'
import Logger from '../../../services/logger.service'
import { resetShiftStartup } from '../../crew/store/crew.actions'
import { resetConfig } from '../../configurations/store/configurations.actions'
import { resetSubmitChartState, updateSubmitChartValidationState } from '../../submitChart/store/submitChart.actions'
import { setPcr, setPcrId } from '../../home/store/home.actions'
import { resetEKGAfterLeavingService } from '../../ekgImport/store/ekgImport.actions'
import { resetPNNVState } from '../../../components/pnnv/store/pnnv.actions'
import { UserTypeConstants } from '../store/userType.constants'
import { resetLogin } from '../store/login.actions'
import { setClientDataError } from '../../clientData/store/clientData.actions'
import { resetSession } from '../../session/resume/store/sessionResume.actions'
import sessionResumeService from '../../session/resume/service/sessionResume.service'
import { resetQaAddendumState } from '../../qaFlags/qaAddendums/store/qaAddendum.actions'
import { resetNotificationCenterState } from '../../notificationCenter/store/notificationCenter.actions'

const AUTH_STATE_MAP_KEY = 'authStateMap'
const AUTHENTICATION_URL = `${zollBaseURL}/Authentication`
const GET_USER_TYPE_URL = `${zollBaseURL}/api/DatabaseAmb/get-user-type`
const LOGIN_URL = `${zollBaseURL}/api/Login/login`

export class AuthenticationService extends ZollAPIService {
  constructor() {
    super('')
  }

  async doAuthentication(data: string | unknown): Promise<AuthenticationResponse> {
    return this.postWithError(AUTHENTICATION_URL, data, false)
  }

  async doGetUserType(data: string | unknown): Promise<GetUserTypeResponse> {
    return this.postWithError(GET_USER_TYPE_URL, data, false)
  }

  async doLogin(data: string | unknown): Promise<AuthenticationResponse> {
    return this.postWithError(LOGIN_URL, data, false)
  }

  static authStateIsAuthenticated = (authState: AuthState | undefined | null): boolean => {
    if (authState === undefined || authState === null || authState.isAuthenticated === undefined) {
      return false
    }

    return authState.isAuthenticated
  }

  static allowSecureRoutes = (user: IUserState): boolean => {
    let authState = user.authState

    if (isOffline() && !authState) {
      authState = AuthenticationService.getOfflineAuthState(user.userName)
    }

    return AuthenticationService.authStateIsAuthenticated(authState)
  }

  static setAuthStateMap = (authState: AuthState) => {
    const authStateMapObj = localStorage.getItem(AUTH_STATE_MAP_KEY)
    if (authStateMapObj && Object.keys(authStateMapObj).length !== 0) {
      const authStateMap: Map<string, AuthState> = new Map(JSON.parse(authStateMapObj))
      AuthenticationService.setAuthToken(authState, authStateMap)
    } else {
      const authStateMap: Map<string, AuthState> = new Map()
      AuthenticationService.setAuthToken(authState, authStateMap)
    }
  }

  static setAuthToken = (authState: AuthState, authStateMap: Map<string, AuthState>) => {
    const username = authState.idToken?.claims.sub?.toLowerCase()
    const preferredUsername = authState.idToken?.claims.preferred_username?.toLowerCase()

    if (preferredUsername) {
      AuthenticationService.setAuthStateForUser(preferredUsername, authStateMap, authState)

      if (username && username !== preferredUsername) {
        AuthenticationService.deleteAuthStateForUser(username, authStateMap)
      }
      localStorage.setItem(AUTH_STATE_MAP_KEY, JSON.stringify(Array.from(authStateMap.entries())))

      return
    }

    // This is backwards compatibility code that was put in during 11.0 for MOB-15245
    // All new online tokens will have the preferred_username claim
    // We can take this out eventually (once we are in 14.0 is probably safe)
    if (username) {
      AuthenticationService.setAuthStateForUser(username, authStateMap, authState)

      localStorage.setItem(AUTH_STATE_MAP_KEY, JSON.stringify(Array.from(authStateMap.entries())))
    }
  }

  static getOfflineAuthState = (username: string): AuthState | null => {
    const authStateMapObj = localStorage.getItem(AUTH_STATE_MAP_KEY)

    if (authStateMapObj && Object.keys(authStateMapObj).length !== 0) {
      const authStateMap: Map<string, AuthState> = new Map(JSON.parse(authStateMapObj))
      const authState = AuthenticationService.getAuthStateForUser(username, authStateMap)

      if (authState !== undefined) {
        return authState
      }
    }

    return null
  }

  static setOfflineAuthState = (username: string, isAuthenticated: boolean): AuthState | null => {
    const authState = AuthenticationService.getOfflineAuthState(username)

    if (authState === null) {
      Logger.error(`Can't find authState for username: ${username}`)
      return null
    }

    authState.isAuthenticated = isAuthenticated
    AuthenticationService.setAuthStateMap(authState)

    return authState
  }

  private static getAuthStateForUser = (
    username: string,
    authStateMap: Map<string, AuthState>
  ): AuthState | undefined => {
    return authStateMap?.get(username?.toLowerCase())
  }

  private static setAuthStateForUser = (
    username: string,
    authStateMap: Map<string, AuthState>,
    authState: AuthState
  ): void => {
    authStateMap?.set(username?.toLowerCase(), authState)
  }

  private static deleteAuthStateForUser = (username: string, authStateMap: Map<string, AuthState>): void => {
    authStateMap?.delete(username?.toLowerCase())
  }

  private static doFederatedLogout = async (user: IUserState) => {
    if (UserTypeConstants.USER_TYPE_SSO !== user.userTypeData?.userType) {
      return
    }

    const userName = user.userName

    const federatedClaims = decodeAccessToken(user.tempOktaAccessToken)
    const logoutUrl = federatedClaims?.logout_url
    const isFederated = !!logoutUrl

    Logger.log(`User: ${userName} is federated: ${isFederated}`)

    // For federated users, we need to redirect them to a logoutUrl in the mobile browser
    if (isFederated) {
      Logger.log(`Federated user: ${userName} is being redirected to ${logoutUrl}`)
      window.open(logoutUrl, '_system')
    }
  }

  static doLogout = (user: IUserState, dispatch: Dispatch<any>): void => {
    const userName = user.userName
    Logger.log(`Starting logout for user: ${userName}`)

    sessionResumeService.stopSession()
    dispatch(resetSession())

    dispatch(updateSubmitChartValidationState())
    dispatch(resetUser())
    dispatch(resetShiftStartup())
    dispatch(resetEKGAfterLeavingService())
    dispatch(resetConfig())
    dispatch(resetSubmitChartState())
    dispatch(resetPNNVState())
    dispatch(resetQaAddendumState())
    dispatch(resetNotificationCenterState())
    dispatch(setPcr(null))
    dispatch(setPcrId(null))
    dispatch(resetLogin())
    dispatch(setClientDataError(null))

    if (!userName) {
      Logger.warn('Logout called in an invalid state (no userName)')
      return
    }

    AuthenticationService.setOfflineAuthState(userName, false)

    // -------------------------------------
    // !!!!!!!!!!!!! IMPORTANT !!!!!!!!!!!!!
    // -------------------------------------
    // This needs to be the very LAST step we do in logout
    AuthenticationService.doFederatedLogout(user)
  }
}
